import React from 'react';

import { Text } from '@change/ds-core-ui/Text';
import { cn } from '@change/ds-utils';

import { VerifiedAvatar } from '../../shared/components/VerifiedAvatar';

import { useEndorsersList } from './hooks/useEndorsersList';
import type { Endorsement, VoterGuidePick } from './hooks/useEndorsersList';

const Endorser: React.FC<{
	endorsement: Endorsement | VoterGuidePick;
	type: 'endorsement' | 'voterGuidePick';
	// eslint-disable-next-line complexity
}> = ({ endorsement, type }) => {
	let name;
	let photoUrl;

	if (type === 'endorsement') {
		const { person, organization } = endorsement as Endorsement;
		name = person?.displayName ?? organization?.name ?? 'Endorser';
		photoUrl = person?.profilePhoto?.photoUrl ?? organization?.profilePhoto?.photoUrl;
	} else {
		const { voterGuide } = endorsement as VoterGuidePick;
		name = voterGuide.author.displayName;
		photoUrl = voterGuide.author.profilePhoto?.photoUrl;
	}

	return (
		<div className="flex flex-row items-center gap-2">
			<VerifiedAvatar avatarUrl={photoUrl || ''} />
			<Text variant="body" size="small" className="text-typography-strong">
				{name}
			</Text>
		</div>
	);
};

export const EndorsersList: React.FC<EmptyProps> = () => {
	const {
		data: { voterGuidePicks, endorsements, totalRecommendations, headingText },
	} = useEndorsersList();
	if (totalRecommendations === 0) return null;

	const isSingleColumn = totalRecommendations <= 6;

	return (
		<div data-testid="dm-endorsements-section-list-container">
			<div className="mt-14 mb-5">
				<Text as="h3" variant="body" size="small" className="text-typography-strong" fontWeight="bold">
					{headingText}
				</Text>
			</div>
			<div className={cn('grid grid-cols-1 gap-4', !isSingleColumn && 'md:grid-cols-2')}>
				{voterGuidePicks?.map((pick) => (
					<Endorser key={`PickEndorsementName_${pick.id}`} endorsement={pick} type="voterGuidePick" />
				))}
				{endorsements?.map((endorsement) => (
					<Endorser key={`EndorsementName_${endorsement.id}`} endorsement={endorsement} type="endorsement" />
				))}
			</div>
		</div>
	);
};
